import { useEffect, useRef, type JSX } from 'react';
import type { Breath } from '../lib/useBreath';

export interface HalftoneWaveProps {
  breath: Breath;
  playing: boolean;
  working: boolean;
  width?: number;
  height?: number;
}

const COLS = 44;

function rgba(hex: string, a: number): string {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  let h = m[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

export function HalftoneWave(props: HalftoneWaveProps): JSX.Element {
  const width = props.width ?? 320;
  const height = props.height ?? 56;

  const canvasRef = useRef<HTMLCanvasElement>(null);

  const breathRef = useRef(props.breath);
  breathRef.current = props.breath;
  const playingRef = useRef(props.playing);
  playingRef.current = props.playing;
  const workingRef = useRef(props.working);
  workingRef.current = props.working;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let ink = '#baccd9';
    const readTheme = () => {
      ink = getComputedStyle(document.documentElement).getPropertyValue('--dim').trim() || ink;
    };
    readTheme();
    const observer = new MutationObserver(readTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    const spectrum = new Uint8Array(COLS);
    const amps = new Float32Array(COLS);
    const ys = new Float32Array(COLS);

    const margin = 2 * dpr;
    const step = (canvas.width - margin * 2) / (COLS - 1);
    const baseY = canvas.height * 0.86;
    const rise = canvas.height * 0.66;

    // a miniature ink ridge: 淡墨勾脊，脊下渐隐成雾脚，两端没入雾中
    const drawFrame = (t: number, live: boolean) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const playing = playingRef.current;
      const working = workingRef.current;

      for (let i = 0; i < COLS; i++) {
        const x01 = i / (COLS - 1);
        const env = 0.15 + 0.85 * Math.sin(Math.PI * x01) ** 0.75;
        const hill = Math.sin(i * 0.48 + 1.2) * 0.5 + 0.5;
        let v: number;
        if (playing && live) {
          v = 0.14 + (amps[i] / 255) * 0.86;
        } else if (playing) {
          // no analyser (mobile keeps the <audio> bare for background playback) — gentle pulse
          v = 0.2 + 0.22 * (Math.sin(i * 0.5 - t * 0.0014) * 0.5 + 0.5) * hill;
        } else if (working) {
          v = 0.18 + 0.14 * (Math.sin(i * 0.42 + t * 0.0009) * 0.5 + 0.5);
        } else {
          v = 0.16 + 0.12 * hill;
        }
        ys[i] = baseY - v * rise * env;
      }

      ctx.beginPath();
      ctx.moveTo(margin, ys[0]);
      for (let i = 1; i < COLS; i++) {
        const x0 = margin + (i - 1) * step;
        const x1 = margin + i * step;
        ctx.quadraticCurveTo(x0, ys[i - 1], (x0 + x1) / 2, (ys[i - 1] + ys[i]) / 2);
      }
      ctx.lineTo(margin + (COLS - 1) * step, ys[COLS - 1]);
      ctx.strokeStyle = rgba(ink, playing && live ? 0.58 : 0.4);
      ctx.lineWidth = 1 * dpr;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.stroke();

      ctx.lineTo(canvas.width - margin, canvas.height);
      ctx.lineTo(margin, canvas.height);
      ctx.closePath();
      const g = ctx.createLinearGradient(0, canvas.height * 0.25, 0, canvas.height);
      g.addColorStop(0, rgba(ink, 0.32));
      g.addColorStop(1, rgba(ink, 0));
      ctx.fillStyle = g;
      ctx.fill();
    };

    const mq = matchMedia('(prefers-reduced-motion: reduce)');
    let raf = 0;
    let hidden = false;

    const drawStatic = () => {
      for (let i = 0; i < COLS; i++) amps[i] = 0;
      drawFrame(0, false);
    };

    const loop = () => {
      const frame = (now: number) => {
        const live = breathRef.current.getSpectrum(spectrum);
        for (let i = 0; i < COLS; i++) {
          amps[i] = amps[i] * 0.72 + spectrum[i] * 0.28;
        }
        drawFrame(now, live);
        raf = requestAnimationFrame(frame);
      };
      raf = requestAnimationFrame(frame);
    };

    const stop = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };

    const start = () => {
      stop();
      if (mq.matches) {
        drawStatic();
        return;
      }
      if (hidden) return;
      loop();
    };

    const onVisibility = () => {
      hidden = document.hidden;
      if (hidden) stop();
      else start();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const onReducedChange = () => start();
    mq.addEventListener('change', onReducedChange);

    start();

    return () => {
      stop();
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      mq.removeEventListener('change', onReducedChange);
    };
  }, [width, height]);

  return <canvas aria-hidden style={{ width, height, display: 'block', pointerEvents: 'none' }} ref={canvasRef} />;
}
